import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from './api.service';

export interface AuthUser {
  publicId: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  dni: string;
  email: string;
  phone: string;
  password: string;
  departmentId?: string;
  provinceId?: string;
  districtId?: string;
  address?: string;
}

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

export interface CustomerProfile {
  publicId: string;
  firstName: string;
  lastName: string;
  dni: string;
  email: string;
  phone: string;
  birthdate: string | null;
  position: string | null;
  marketValue: string | null;
  nickname: string | null;
  departmentId: string | null;
  provinceId: string | null;
  districtId: string | null;
  department: string | null;
  province: string | null;
  district: string | null;
  address: string | null;
  profileImageUrl: string | null;
  deliveryType: { id: number; code: string; name: string } | null;
  isAccountEnabled: boolean;
  createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {

  private readonly authUrl = environment.authUrl;

  currentUser = signal<AuthUser | null>(this.loadUser());
  customerProfile = signal<CustomerProfile | null>(null);
  isLoggedIn = signal<boolean>(!!this.getToken());
  showLoginModal = signal<boolean>(false);

  constructor(private http: HttpClient) {
    if (this.getToken()) {
      this.loadProfile();
    }
  }

  login(data: LoginRequest): Observable<ApiResponse<AuthResponse>> {
    return this.http.post<ApiResponse<AuthResponse>>(`${this.authUrl}/customer/login`, data).pipe(
      tap(res => {
        if (res.success) {
          this.saveSession(res.data);
          this.loadProfile();
        }
      })
    );
  }

  register(data: RegisterRequest): Observable<ApiResponse<AuthResponse>> {
    return this.http.post<ApiResponse<AuthResponse>>(`${this.authUrl}/customer/register`, data).pipe(
      tap(res => {
        if (res.success) {
          this.saveSession(res.data);
          this.loadProfile();
        }
      })
    );
  }

  logout(): void {
    const token = this.getToken();
    if (token) {
      this.http.post(`${this.authUrl}/customer/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).subscribe();
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
    this.currentUser.set(null);
    this.customerProfile.set(null);
    this.isLoggedIn.set(false);
  }

  getToken(): string | null {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem('accessToken');
  }

  getRefreshToken(): string | null {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem('refreshToken');
  }

  refreshToken(): Observable<ApiResponse<AuthResponse>> {
    const token = this.getToken();
    const refreshToken = this.getRefreshToken();

    if (!token || !refreshToken) {
      return new Observable(subscriber => {
        subscriber.error(new Error('No tokens available'));
      });
    }

    const sessionId = this.getSessionIdFromToken(token);
    if (!sessionId) {
      return new Observable(subscriber => {
        subscriber.error(new Error('Invalid token'));
      });
    }

    return this.http.post<ApiResponse<AuthResponse>>(`${this.authUrl}/customer/refresh`, {
      sessionId,
      refreshToken,
    }).pipe(
      tap(res => {
        if (res.success) {
          this.saveSession(res.data);
        }
      })
    );
  }

  private getSessionIdFromToken(token: string): string | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.jti ?? null;
    } catch {
      return null;
    }
  }

  getProfile(): Observable<ApiResponse<CustomerProfile>> {
    return this.http.get<ApiResponse<CustomerProfile>>(`${this.authUrl}/customer/me`, {
      headers: { Authorization: `Bearer ${this.getToken()}` }
    });
  }

  loadProfile(): void {
    this.getProfile().subscribe({
      next: res => {
        if (res.success) {
          this.customerProfile.set(res.data);
        }
      }
    });
  }

  private saveSession(data: AuthResponse): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    this.currentUser.set(data.user);
    this.isLoggedIn.set(true);
  }

  private loadUser(): AuthUser | null {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  }
}
